export type VoiceCaptureUiLocale = 'en' | 'es';

export interface VoiceCaptureMessages {
  successBadge: string;
  transcriptLabel: string;
  transcriptPlaceholder: string;
  startRecording: string;
  stopRecording: string;
  processTranscript: string;
  retryMicrophoneAccess: string;
  retryAction: string;
  recordAgain: string;
  idleStatus: string;
  recordingStatus: string;
  capturedStatus: string;
  processingStatus: string;
  doneStatus: string;
  permissionDeniedStatus: string;
  requestErrorStatus: string;
  recognitionErrorStatus: string;
  permissionDeniedMessage: string;
  unsupportedMessage: string;
  noMicrophoneMessage: string;
  noSpeechMessage: string;
  networkMessage: string;
  abortedMessage: string;
  recognitionMessage: string;
  requestErrorMessage: string;
  missingContextMessage: string;
}

export type VoiceCaptureMessageOverrides = Partial<VoiceCaptureMessages>;

export const voiceCaptureMessagesByLocale: Record<VoiceCaptureUiLocale, VoiceCaptureMessages> = {
  en: {
    successBadge: '✓ Text processed successfully',
    transcriptLabel: 'Editable transcript',
    transcriptPlaceholder: 'Your transcript will appear here. You can edit it before processing.',
    startRecording: 'Start recording',
    stopRecording: 'Stop',
    processTranscript: 'Process',
    retryMicrophoneAccess: 'Retry microphone access',
    retryAction: 'Retry',
    recordAgain: 'Record again',
    idleStatus: 'Start recording to capture speech and turn it into editable text.',
    recordingStatus: 'Listening continuously. Live interim results are shown in the textarea.',
    capturedStatus: 'Review the transcript, edit it if needed, and then process it.',
    processingStatus: 'Processing transcript...',
    doneStatus: 'The transcript was processed successfully. You can record again if needed.',
    permissionDeniedStatus: 'Microphone access is blocked until the browser permission is updated.',
    requestErrorStatus: 'The API request failed. Fix the issue and retry.',
    recognitionErrorStatus: 'Speech recognition failed. Retry or record again.',
    permissionDeniedMessage:
      'Microphone access was denied. Review the browser site permission and try again.',
    unsupportedMessage: 'This browser does not support the Web Speech API.',
    noMicrophoneMessage: 'No microphone is available on this device.',
    noSpeechMessage: 'No speech was detected.',
    networkMessage: 'A network error interrupted speech recognition.',
    abortedMessage: 'Speech recognition was aborted.',
    recognitionMessage: 'Speech recognition failed.',
    requestErrorMessage: 'The request failed while processing the transcript.',
    missingContextMessage: 'The context property is required before processing text.',
  },
  es: {
    successBadge: '✓ Texto procesado correctamente',
    transcriptLabel: 'Transcripción editable',
    transcriptPlaceholder:
      'La transcripción aparecerá aquí. Puedes editarla antes de procesarla.',
    startRecording: 'Iniciar grabación',
    stopRecording: 'Detener',
    processTranscript: 'Procesar',
    retryMicrophoneAccess: 'Reintentar acceso al micrófono',
    retryAction: 'Reintentar',
    recordAgain: 'Grabar de nuevo',
    idleStatus: 'Inicia la grabación para capturar voz y convertirla en texto editable.',
    recordingStatus:
      'Escuchando de forma continua. Los resultados intermedios en vivo se muestran en el textarea.',
    capturedStatus: 'Revisa la transcripción, edítala si es necesario y luego procésala.',
    processingStatus: 'Procesando transcripción...',
    doneStatus: 'La transcripción se procesó correctamente. Puedes grabar de nuevo si lo necesitas.',
    permissionDeniedStatus:
      'El acceso al micrófono está bloqueado hasta que actualices el permiso del navegador.',
    requestErrorStatus: 'La solicitud al API falló. Corrige el problema y reintenta.',
    recognitionErrorStatus: 'El reconocimiento de voz falló. Reintenta o vuelve a grabar.',
    permissionDeniedMessage:
      'Se denegó el acceso al micrófono. Revisa el permiso del sitio en el navegador y vuelve a intentarlo.',
    unsupportedMessage: 'Este navegador no soporta la Web Speech API.',
    noMicrophoneMessage: 'No hay un micrófono disponible en este dispositivo.',
    noSpeechMessage: 'No se detectó voz.',
    networkMessage: 'Un error de red interrumpió el reconocimiento de voz.',
    abortedMessage: 'El reconocimiento de voz fue cancelado.',
    recognitionMessage: 'El reconocimiento de voz falló.',
    requestErrorMessage: 'La solicitud falló mientras se procesaba la transcripción.',
    missingContextMessage: 'La property context es obligatoria antes de procesar el texto.',
  },
};

export const normalizeVoiceCaptureLocale = (locale: string): VoiceCaptureUiLocale => {
  const normalizedLocale = locale.trim().toLowerCase();

  if (normalizedLocale.startsWith('es')) {
    return 'es';
  }

  return 'en';
};

export const resolveVoiceCaptureMessages = (
  locale: string,
  overrides: VoiceCaptureMessageOverrides = {},
): VoiceCaptureMessages => {
  const baseMessages = voiceCaptureMessagesByLocale[normalizeVoiceCaptureLocale(locale)];

  return {
    ...baseMessages,
    ...overrides,
  };
};

